import { getActiveLineItemLabelApi } from 'api/prescription';
import { hasValue } from './ScanSessionContext';

// What the Rx scan station knows about the printed sheet a session was opened
// from — its barcode id (labelSId) and the LOT it prints — so that after the
// commit fulfillCommittedSession (rxScanValidation.js) can tell whether core
// retired that sheet and reprint.

// A session opened by printed Rx number has neither the sheet's barcode id nor
// its printed LOT — listPomOrders carries neither — so after the commit the
// portal could not tell a lot change from a match, nor check whether core
// retired the sheet. So the live label is fetched in the background as soon as
// the record resolves (nothing waits on it), and fulfillCommittedSession reads
// it at commit. Kept here rather than in session state, which is persisted to
// localStorage. Keyed by line item; a re-open of the same Rx replaces it.
const activeLabelLookups = new Map();

export const startActiveLabelLookup = (lineItemId) => {
  const lookup = { settled: false, label: null };
  activeLabelLookups.set(lineItemId, lookup);
  getActiveLineItemLabelApi({ lineItemId })
    .then((label) => {
      lookup.label = label;
    })
    .catch(() => {
      lookup.label = null;
    })
    .finally(() => {
      lookup.settled = true;
    });
};

// What the portal knows about the printed sheet for this commit, read BEFORE
// the fulfillment is sent: an answer that lands after it could already
// describe the label core regenerated for this very commit.
//  - 'barcode': opened from the barcode; rx already carries labelSId and the
//    printed LOT.
//  - 'known':   opened by Rx number and the lookup came back with a live label;
//    `rx` is the record with that label's labelSId / printed LOT filled in.
//  - 'unknown': opened by Rx number and the lookup failed, found no live label
//    or had not answered yet — the portal cannot tell whether the sheet is
//    still right.
export const takePrintedLabelInfo = (committed) => {
  if (hasValue(committed.rx?.labelSId)) return { status: 'barcode', rx: committed.rx };
  const lineItemId = committed.rx?.lineItemId;
  const lookup = lineItemId ? activeLabelLookups.get(lineItemId) : undefined;
  if (lineItemId) activeLabelLookups.delete(lineItemId);
  if (!lookup?.settled || !lookup.label) return { status: 'unknown', rx: committed.rx };
  const { labelSId, lotNumber } = lookup.label;
  return {
    status: 'known',
    rx: {
      ...committed.rx,
      ...(hasValue(labelSId) ? { labelSId } : {}),
      ...(hasValue(lotNumber) ? { printedLotNumber: lotNumber } : {}),
    },
  };
};

// Set of lot numbers compared the way core compares them (isSameLotSet):
// trimmed, case-insensitive, order and repeats ignored.
const lotKeySet = (lotNumbers) =>
  new Set(
    lotNumbers
      .map((lot) =>
        String(lot ?? '')
          .trim()
          .toLowerCase()
      )
      .filter(Boolean)
  );

// True when the session dispenses lots other than the ones printed on the
// sheet it was opened from. Core regenerates the label for that (the LOT, and
// the BUD derived from it, change), so the portal reprints straight away, the
// same as for an NDC substitution. Only knowable when the printed LOT came back
// with the scan; a sheet with no LOT (dispense flow OFF, or no lot recorded
// yet) falls to confirmPrintedLabelStillLive instead. A session opened by
// printed Rx number gets its printed LOT from the background label lookup.
export const sessionHasLotChange = (session) => {
  const printed = session?.rx?.printedLotNumber;
  if (!hasValue(printed)) return false;
  const printedLots = lotKeySet(printed.split(','));
  const sessionLots = lotKeySet((session?.vials ?? []).map((v) => v.fields?.lotNumber));
  return (
    printedLots.size !== sessionLots.size || [...sessionLots].some((lot) => !printedLots.has(lot))
  );
};
